import React, { useState, useEffect } from 'react';
import Button from '../ui/Button.jsx';
import Input from '../ui/Input.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.jsx';
import { useFamily } from '../context/FamilyContext.jsx';
import { FAMILY_CODE_KEY, MEMBER_SECRET_KEY } from '../constants.js';
import PinInput from './PinInput.jsx';
import QuestionnaireInfoStep, { QUESTIONNAIRE_INFO_STEP_IDS } from './QuestionnaireInfoStep.jsx';

function normalizeInviteCode(value) {
  return String(value || '').trim().toUpperCase();
}

export default function Onboarding({ initialInviteCode = '', embedded = false, onActiveChange }) {
  const { createFamily, joinFamily, reconnect, requestMagicLink } = useFamily();
  const normalizedInitialInviteCode = normalizeInviteCode(initialInviteCode);
  const [mode, setMode] = useState(normalizedInitialInviteCode ? 'purpose' : 'intro'); // intro, purpose, join, reconnect, create_names, create_pin, join_names, join_pin, join_direct
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purposeNextMode, setPurposeNextMode] = useState(
    normalizedInitialInviteCode ? 'join_direct' : null
  );

  // User input state
  const [familyName, setFamilyName] = useState('');
  const [memberName, setMemberName] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');

  // For reconnect
  const [reconnectEmail, setReconnectEmail] = useState('');
  const [memberSecret, setMemberSecret] = useState('');
  const [magicLinkMessage, setMagicLinkMessage] = useState('');

  // Check for existing credentials
  useEffect(() => {
      const code = localStorage.getItem(FAMILY_CODE_KEY);
      const secret = localStorage.getItem(MEMBER_SECRET_KEY);
      if (normalizedInitialInviteCode) {
          setInviteCode(normalizedInitialInviteCode);
          setPurposeNextMode('join_direct');
          setMode('purpose');
      } else if (code) {
          setInviteCode(code);
      }
      if (secret) setMemberSecret(secret);
  }, [normalizedInitialInviteCode]);

  // Notify parent when the user enters/leaves a sub-flow (anything but the intro screen),
  // so a host page (e.g. the landing) can hide its surrounding content.
  useEffect(() => {
      if (typeof onActiveChange === 'function') {
          onActiveChange(mode !== 'intro');
      }
  }, [mode, onActiveChange]);

  // --- CREATE FLOW ---

  const handleCreateStart = () => {
      setFamilyName('');
      setMemberName('');
      setError(null);
      setPurposeNextMode('create_names');
      setMode('purpose');
  };

  const handleCreateNamesSubmit = (e) => {
      e.preventDefault();
      if (!memberName.trim()) {
          setError('Votre prénom est obligatoire');
          return;
      }
      setMode('create_pin');
      setPinCode('');
      setPinConfirm('');
      setError(null);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (pinCode.length < 4) {
        setError("Le code secret doit contenir 4 chiffres");
        return;
    }
    if (pinCode !== pinConfirm) {
        setError("Les deux codes ne correspondent pas");
        return;
    }

    setLoading(true);
    setError(null);
    try {
      await createFamily({
          name: familyName || 'Mon foyer',
          member_name: memberName,
          secret: pinCode
      });
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // --- JOIN FLOW ---

  const handleJoinStart = () => {
      setInviteCode(normalizedInitialInviteCode || '');
      setMemberName('');
      setPinCode('');
      setError(null);
      setPurposeNextMode('join_direct');
      setMode('purpose');
  };

  const handlePurposeContinue = () => {
    setError(null);
    setMode(purposeNextMode || 'intro');
  };

  const handleJoinCodeSubmit = (e) => {
      e.preventDefault();
      if (!inviteCode || inviteCode.length < 6) {
          setError("Code d'invitation invalide");
          return;
      }
      setMode('join_names');
      setMemberName('');
      setError(null);
  };

  const handleJoinNamesSubmit = (e) => {
      e.preventDefault();
      if (!memberName.trim()) {
          setError('Votre prénom est obligatoire');
          return;
      }
      setMode('join_pin');
      setPinCode('');
      setError(null);
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (!inviteCode || inviteCode.length < 6) {
        setError("Code d'invitation invalide");
        return;
    }
    if (!memberName.trim()) {
        setError('Votre prénom est obligatoire');
        return;
    }
    if (pinCode.length < 4) {
        setError("Le code secret doit contenir 4 chiffres");
        return;
    }

    setLoading(true);
    setError(null);
    try {
      await joinFamily({
          invite_code: inviteCode,
          member_name: memberName,
          secret: pinCode
      });
    } catch (err) {
        if (err.message && err.message.includes('FAMILY_FULL')) {
            setError("Ce foyer est complet (2 membres). Utilisez la reconnexion si vous êtes déjà membre.");
            setTimeout(() => setMode('reconnect'), 2000);
        } else {
            setError(err.message || 'Code invalide ou erreur serveur');
        }
    } finally {
      setLoading(false);
    }
  };

  const handleDirectJoinSubmit = async (e) => {
    e.preventDefault();
    await handleJoinSubmit(e);
  };

  // --- RECONNECT FLOW ---

  const handleReconnect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMagicLinkMessage('');
    try {
        await reconnect({
            invite_code: inviteCode,
            member_secret: memberSecret
        });
    } catch (err) {
        setError(err.message || 'Impossible de se reconnecter. Vérifiez vos informations.');
    } finally {
        setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMagicLinkMessage('');

    try {
      const payload = await requestMagicLink({
        email: reconnectEmail
      });
      setMagicLinkMessage(payload?.message || 'Si cette adresse est connue et vérifiée, un lien de reconnexion vient d’être envoyé.');
    } catch (err) {
      setError(err.message || 'Impossible d’envoyer le lien de reconnexion.');
    } finally {
      setLoading(false);
    }
  };

  const mediumContainerClass = embedded ? 'max-w-md mx-auto' : 'max-w-md mx-auto mt-10 px-4';
  const wideContainerClass = embedded ? 'max-w-2xl mx-auto' : 'max-w-2xl mx-auto mt-10 px-4';
  const introContainerClass = embedded ? 'max-w-3xl mx-auto' : 'max-w-xl mx-auto mt-10 px-4 text-center space-y-8';

  // --- RENDERERS ---

  if (mode === 'purpose') {
      return (
        <div className={wideContainerClass}>
            <Card className="overflow-hidden">
                <CardContent className="space-y-6">
                    <QuestionnaireInfoStep stepId={QUESTIONNAIRE_INFO_STEP_IDS.purpose} />
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                        <Button type="button" variant="outline" onClick={() => setMode('intro')}>
                            Retour
                        </Button>
                        <Button type="button" onClick={handlePurposeContinue}>
                            Continuer
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
      );
  }

  if (mode === 'create_names') {
      return (
        <div className={mediumContainerClass}>
            <Card className="overflow-hidden">
                <CardHeader>
                    <div className="ptcm-section-pill w-fit">Créer un foyer</div>
                    <CardTitle className="mt-4 text-2xl">Commençons par les présentations</CardTitle>
                    <CardDescription>
                        Dites-nous comment vous vous appelez.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleCreateNamesSubmit} className="space-y-4">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-ptcm-ink dark:text-gray-200">Votre prénom (ou surnom)</label>
                            <Input
                                type="text"
                                placeholder="ex: Camille"
                                value={memberName}
                                onChange={(e) => setMemberName(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-ptcm-ink dark:text-gray-200">Nom du foyer (optionnel)</label>
                            <Input
                                type="text"
                                placeholder="ex: Maison du Bonheur"
                                value={familyName}
                                onChange={(e) => setFamilyName(e.target.value)}
                            />
                        </div>
                        {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-500">{error}</div>}
                        <div className="flex gap-2 pt-2">
                            <Button type="button" variant="outline" onClick={() => setMode('intro')} className="flex-1">
                                Retour
                            </Button>
                            <Button type="submit" disabled={!memberName.trim()} className="flex-1">
                                Suivant
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
      );
  }

  if (mode === 'create_pin') {
      return (
        <div className={mediumContainerClass}>
            <Card className="overflow-hidden">
                <CardHeader>
                    <div className="ptcm-section-pill w-fit">Créer un foyer</div>
                    <CardTitle className="mt-4 text-2xl">Sécuriser votre accès</CardTitle>
                    <CardDescription>
                        Bonjour {memberName} ! Choisissez un code secret à 4 chiffres.<br/>
                        Il vous servira à retrouver votre compte sur un autre appareil.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleCreateSubmit} className="space-y-6">
                        <div className="flex flex-col items-center">
                            <label className="block text-sm font-medium mb-3">Mon Code Secret</label>
                            <PinInput
                                length={4}
                                value={pinCode}
                                onChange={setPinCode}
                                disabled={loading}
                            />
                            <p className="mt-3 text-center text-xs text-ptcm-muted">
                                Notez-le précieusement ! C'est le seul moyen de récupérer votre accès.
                            </p>
                        </div>
                        <div className="flex flex-col items-center">
                            <label className="block text-sm font-medium mb-3">Confirmez votre code</label>
                            <PinInput
                                length={4}
                                value={pinConfirm}
                                onChange={setPinConfirm}
                                disabled={loading}
                            />
                        </div>
                        {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-center text-sm text-red-500">{error}</div>}
                        <div className="flex gap-2 pt-2">
                            <Button type="button" variant="outline" onClick={() => setMode('create_names')} className="flex-1">
                                Retour
                            </Button>
                            <Button type="submit" disabled={pinCode.length < 4 || pinConfirm.length < 4 || loading} className="flex-1">
                                {loading ? 'Création...' : 'Créer le foyer'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
      );
  }

  if (mode === 'join') {
    return (
      <div className={mediumContainerClass}>
        <Card className="overflow-hidden">
          <CardHeader>
            <div className="ptcm-section-pill w-fit">Invitation</div>
            <CardTitle className="mt-4 text-2xl">Rejoindre un foyer</CardTitle>
            <CardDescription>Entrez le code d'invitation partagé par votre partenaire.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleJoinCodeSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-ptcm-ink">Code d'invitation</label>
                <Input
                  type="text"
                  className="text-center text-lg font-semibold uppercase tracking-[0.35em]"
                  placeholder="ABC12345"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  autoFocus
                />
              </div>
              {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-500">{error}</div>}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setMode('intro')} className="flex-1">
                  Retour
                </Button>
                <Button type="submit" disabled={!inviteCode || loading} className="flex-1">
                  Suivant
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'join_names') {
      return (
        <div className={mediumContainerClass}>
            <Card className="overflow-hidden">
                <CardHeader>
                    <div className="ptcm-section-pill w-fit">Invitation</div>
                    <CardTitle className="mt-4 text-2xl">Qui êtes-vous ?</CardTitle>
                    <CardDescription>
                        Pour que votre partenaire sache qui rejoint le foyer.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleJoinNamesSubmit} className="space-y-4">
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-ptcm-ink">Votre prénom</label>
                            <Input
                                type="text"
                                placeholder="ex: Alex"
                                value={memberName}
                                onChange={(e) => setMemberName(e.target.value)}
                                autoFocus
                            />
                        </div>
                        {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-500">{error}</div>}
                        <div className="flex gap-2 pt-2">
                            <Button type="button" variant="outline" onClick={() => setMode('join')} className="flex-1">
                                Retour
                            </Button>
                            <Button type="submit" disabled={!memberName.trim()} className="flex-1">
                                Suivant
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
      );
  }

  if (mode === 'join_pin') {
    return (
      <div className={mediumContainerClass}>
          <Card className="overflow-hidden">
              <CardHeader>
                  <div className="ptcm-section-pill w-fit">Invitation</div>
                  <CardTitle className="mt-4 text-2xl">Sécuriser votre accès</CardTitle>
                  <CardDescription>
                      Dernière étape, {memberName} !<br/>
                      Définissez votre code secret personnel pour ce foyer.
                  </CardDescription>
              </CardHeader>
              <CardContent>
                  <form onSubmit={handleJoinSubmit} className="space-y-6">
                      <div className="flex flex-col items-center">
                          <label className="block text-sm font-medium mb-3">Mon Code Secret</label>
                          <PinInput
                                length={4}
                                value={pinCode}
                                onChange={setPinCode}
                                disabled={loading}
                            />
                          <p className="mt-3 text-center text-xs text-ptcm-muted">
                              Ne l'oubliez pas, il vous servira à vous reconnecter plus tard.
                          </p>
                      </div>
                      {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-center text-sm text-red-500">{error}</div>}
                      <div className="flex gap-2 pt-2">
                          <Button type="button" variant="outline" onClick={() => setMode('join_names')} className="flex-1">
                              Retour
                          </Button>
                          <Button type="submit" disabled={pinCode.length < 4 || loading} className="flex-1">
                              {loading ? 'Rejoindre...' : 'Rejoindre le foyer'}
                          </Button>
                      </div>
                  </form>
              </CardContent>
          </Card>
      </div>
    );
  }

  if (mode === 'join_direct') {
    return (
      <div className={mediumContainerClass}>
        <Card className="overflow-hidden">
          <CardHeader>
            <div className="ptcm-section-pill w-fit">{normalizedInitialInviteCode ? 'Lien partagé' : 'Invitation'}</div>
            <CardTitle className="mt-4 text-2xl">Rejoindre un foyer</CardTitle>
            <CardDescription>
              {normalizedInitialInviteCode
                ? 'Le code foyer a été rempli automatiquement depuis le lien partagé.'
                : 'Entrez le code partagé par votre partenaire, votre prénom et un code secret personnel.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDirectJoinSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-ptcm-ink">Code foyer</label>
                <Input
                  type="text"
                  className="bg-ptcm-panel/70 text-center text-lg font-semibold uppercase tracking-[0.35em]"
                  placeholder="ABC12345"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(normalizeInviteCode(e.target.value))}
                  maxLength={8}
                  autoFocus={!inviteCode}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-ptcm-ink">Votre prénom</label>
                <Input
                  type="text"
                  placeholder="ex: Alex"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  autoFocus={Boolean(inviteCode)}
                />
              </div>

              <div className="flex flex-col items-center">
                <label className="block text-sm font-medium mb-3">Mon Code Secret</label>
                <PinInput
                  length={4}
                  value={pinCode}
                  onChange={setPinCode}
                  disabled={loading}
                />
                <p className="mt-3 text-center text-xs text-ptcm-muted">
                  Définissez votre code secret personnel pour ce foyer.
                </p>
              </div>

              {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-center text-sm text-red-500">{error}</div>}

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setMode('intro')} className="flex-1">
                  Retour
                </Button>
                <Button
                  type="submit"
                  disabled={!inviteCode || !memberName.trim() || pinCode.length < 4 || loading}
                  className="flex-1"
                >
                  {loading ? 'Rejoindre...' : 'Rejoindre le foyer'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'reconnect') {
      return (
        <div className={mediumContainerClass}>
            <Card className="overflow-hidden">
            <CardHeader>
                <div className="ptcm-section-pill w-fit">Reconnexion</div>
                <CardTitle className="mt-4 text-2xl">Me reconnecter</CardTitle>
                <CardDescription>Retrouvez votre accès membre.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-5">
                  <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-ptcm-ink dark:text-gray-200">Email vérifié</label>
                      <Input
                        type="email"
                        placeholder="vous@exemple.com"
                        value={reconnectEmail}
                        onChange={(e) => setReconnectEmail(e.target.value)}
                      />
                      <p className="mt-2 text-xs text-ptcm-muted">
                        Nous vous enverrons un lien unique pour vous reconnecter sans votre code secret.
                      </p>
                    </div>

                    {magicLinkMessage ? (
                      <div className="rounded-2xl border border-green-200/70 bg-green-50/90 px-4 py-3 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-200">
                        {magicLinkMessage}
                      </div>
                    ) : null}

                    <Button type="submit" disabled={loading || !reconnectEmail} className="w-full">
                      {loading ? 'Envoi...' : 'Recevoir un lien magique'}
                    </Button>
                  </form>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-ptcm-border"></div>
                    <span className="mx-4 flex-shrink-0 text-xs font-semibold uppercase tracking-[0.18em] text-ptcm-muted">Ou avec mon code secret</span>
                    <div className="flex-grow border-t border-ptcm-border"></div>
                  </div>

                  <form onSubmit={handleReconnect} className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-ptcm-ink">Code foyer</label>
                      <Input
                        type="text"
                        className="text-center text-lg font-semibold uppercase tracking-[0.35em]"
                        placeholder="ABC12345"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      />
                    </div>

                    <div className="flex flex-col items-center">
                      <label className="block text-sm font-medium mb-3">Votre Code Secret</label>
                      <PinInput
                        length={4}
                        value={memberSecret}
                        onChange={setMemberSecret}
                        disabled={loading}
                      />
                    </div>

                    {error ? (
                      <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-center text-sm text-red-500">
                        {error}
                      </div>
                    ) : null}

                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setMode('intro')} className="flex-1">
                        Retour
                      </Button>
                      <Button type="submit" disabled={loading || !inviteCode || memberSecret.length < 4} className="flex-1">
                        {loading ? '...' : 'Se reconnecter'}
                      </Button>
                    </div>
                  </form>
                </div>
            </CardContent>
            </Card>
        </div>
      );
  }

  const hasSavedCredentials = localStorage.getItem(FAMILY_CODE_KEY) && localStorage.getItem(MEMBER_SECRET_KEY);

  if (embedded) {
    return (
      <div className={introContainerClass}>
        <Card className="overflow-hidden">
          <CardHeader className="space-y-4">
            <div className="ptcm-section-pill w-fit">
              Démarrage immédiat
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl leading-tight">
                Créez votre foyer ou rejoignez-en un en moins de 2 minutes
              </CardTitle>
              <CardDescription className="text-base">
                Lancez le parcours guidé, définissez votre code secret, puis enchaînez directement sur le questionnaire.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Button
                size="xl"
                onClick={handleCreateStart}
                disabled={loading}
                className="w-full py-8 text-lg flex flex-col gap-1 h-auto"
              >
                <span>Créer un foyer</span>
                <span className="text-xs font-normal opacity-80">Je commence l’aventure</span>
              </Button>

              <Button
                size="xl"
                variant="outline"
                onClick={handleJoinStart}
                className="w-full py-8 text-lg flex flex-col gap-1 h-auto"
              >
                <span>Rejoindre un foyer</span>
                <span className="text-xs font-normal opacity-80">J’ai un code d’invitation</span>
              </Button>
            </div>

            {hasSavedCredentials ? (
              <div className="rounded-[24px] border border-ptcm-border dark:border-gray-800 bg-ptcm-panel/80 dark:bg-gray-950/40 px-4 py-3 text-sm text-ptcm-ink dark:text-gray-300">
                Une session précédente a été trouvée.
                {' '}
                <button
                  type="button"
                  onClick={() => setMode('reconnect')}
                  className="font-semibold text-ptcm-primary dark:text-blue-400 hover:underline"
                >
                  Se reconnecter
                </button>
              </div>
            ) : null}

            {error ? <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-500">{error}</div> : null}

            <div className="pt-1 text-sm text-ptcm-muted dark:text-gray-400">
              <button
                type="button"
                onClick={() => setMode('reconnect')}
                className="underline hover:text-ptcm-ink dark:hover:text-gray-200"
              >
                J’ai déjà un accès / Je veux me reconnecter
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`${introContainerClass} space-y-8`}>
      <div className="space-y-4 text-center">
        <div className="ptcm-section-pill">Paye ta charge mentale !</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-ptcm-ink dark:text-white">Qui fait quoi à la maison ?</h1>
        <p className="text-lg text-ptcm-muted">
          Évaluez la répartition des tâches ménagères et de la charge mentale dans votre couple.
          Simple, rapide, et sans inscription.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Button size="xl" onClick={handleCreateStart} disabled={loading} className="w-full py-8 text-lg flex flex-col gap-1 h-auto">
           <span>🏠 Créer un foyer</span>
           <span className="text-xs font-normal opacity-80">Je commence l'aventure</span>
        </Button>

        <Button size="xl" variant="outline" onClick={handleJoinStart} className="w-full py-8 text-lg flex flex-col gap-1 h-auto">
           <span>🔗 Rejoindre un foyer</span>
           <span className="text-xs font-normal opacity-80">J'ai un code d'invitation</span>
        </Button>
      </div>

      {hasSavedCredentials && (
         <div className="mt-4">
             <Button variant="ghost" onClick={() => setMode('reconnect')} className="text-ptcm-primary dark:text-blue-400">
                 Une session précédente a été trouvée. Se reconnecter ?
             </Button>
         </div>
      )}

      {error && <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-500">{error}</div>}

      <div className="pt-4 text-sm text-ptcm-muted">
        <button onClick={() => setMode('reconnect')} className="underline hover:text-ptcm-ink dark:text-gray-200">
            J'ai déjà un compte / Je veux me reconnecter
        </button>
      </div>
    </div>
  );
}
